export interface PromptRules {
  isEmpty: boolean
  isTooShort: boolean
  hasPoliteLanguage: boolean
  hasContext: boolean
  hasSpecificRequest: boolean
  score: number
  suggestions: string[]
  analogy: string
}

const POLITE_MARKERS = [
  'пожалуйста',
  'будьте добры',
  'помогите',
  'подскажите',
  'спасибо',
  'благодарю'
]

const CONTEXT_MARKERS = [
  'простыми словами',
  'объясни',
  'расскажи',
  'что такое',
  'как работает',
  'почему',
  'зачем',
  'каким образом'
]

const SPECIFIC_REQUEST_MARKERS = [
  'список',
  'пример',
  'пошагово',
  'инструкция',
  'алгоритм',
  'сравни',
  'различия',
  'преимущества',
  'недостатки'
]

const SOVIET_ANALOGIES = [
  'Ваш запрос как телеграмма - краткость хороша, но важна ясность',
  'Представьте, что вы звонните в справочную службу - чем точнее вопрос, тем полезнее ответ',
  'Как в библиотеке - библиотекарь поможет лучше, если знает, что именно вы ищете',
  'Это как заказ в столовой - чем конкретнее просьба, тем лучше результат',
  'Словно инструкция к радиоприемнику - детали важны для понимания'
]

export function analyzePromptStructure(prompt: string): PromptRules {
  const normalizedPrompt = prompt.toLowerCase().trim()
  
  const isEmpty = normalizedPrompt.length === 0
  const isTooShort = normalizedPrompt.length < 10 && !isEmpty
  
  const hasPoliteLanguage = POLITE_MARKERS.some(marker => 
    normalizedPrompt.includes(marker)
  )
  
  const hasContext = CONTEXT_MARKERS.some(marker => 
    normalizedPrompt.includes(marker)
  )
  
  const hasSpecificRequest = SPECIFIC_REQUEST_MARKERS.some(marker => 
    normalizedPrompt.includes(marker)
  )
  
  let score = 0
  const suggestions: string[] = []
  
  if (isEmpty) {
    score = 0
    suggestions.push('Напишите ваш вопрос или запрос')
  } else if (isTooShort) {
    score = 2
    suggestions.push('Добавьте больше контекста к вашему запросу')
    suggestions.push('Укажите, что именно вы хотите узнать')
  } else {
    score = 4 // Base score for non-empty, adequate length prompts
    
    if (hasPoliteLanguage) score += 1
    if (hasContext) score += 2
    if (hasSpecificRequest) score += 2
    if (normalizedPrompt.length > 50) score += 1
    
    // Generate suggestions based on missing elements
    if (!hasPoliteLanguage && score < 8) {
      suggestions.push('Попробуйте добавить вежливые слова как "пожалуйста" или "помогите"')
    }
    
    if (!hasContext && score < 7) {
      suggestions.push('Уточните контекст - объясните, для чего вам нужна информация')
    }
    
    if (!hasSpecificRequest && score < 6) {
      suggestions.push('Сформулируйте конкретную просьбу - что именно вы хотите получить?')
    }
    
    if (normalizedPrompt.length < 30) {
      suggestions.push('Добавьте больше деталей к вашему вопросу')
    }
  }
  
  // Cap the score at 10
  score = Math.min(score, 10)
  
  // Select random analogy
  const analogy = SOVIET_ANALOGIES[Math.floor(Math.random() * SOVIET_ANALOGIES.length)]
  
  return {
    isEmpty,
    isTooShort,
    hasPoliteLanguage,
    hasContext,
    hasSpecificRequest,
    score,
    suggestions: suggestions.slice(0, 3), // Limit to 3 suggestions
    analogy
  }
}